import React, { useContext, useEffect, useState } from "react";
import { MdArrowForward, MdCheck, MdOutlineEdit } from "react-icons/md";
import { QuizType } from "utils/interfaces";
import { checkResultComplete } from "services/resultsRequests";
import { BACKEND_URL } from "config/getUrl";
import { LessonsContext, MainContext } from "components/utils/Contexts";
import { usePlatform } from "components/utils/PlatformContext";
import ImageSkeleton from "utils/skeletons/ImageSkeleton";

interface QuizListCardProps {
  quiz: QuizType;
  handleExpand: (event: React.MouseEvent) => void;
  handleEdit: (event: React.MouseEvent) => void;
}

const QuizListCard: React.FC<QuizListCardProps> = ({
  quiz,
  handleExpand,
  handleEdit,
}) => {
  // States definition
  const [isComplete, setIsComplete] = useState();

  // Get the platform context
  const { logo } = usePlatform();

  // Get the context to prevent caching
  const { version } = useContext(LessonsContext);
  const { userRole } = useContext(MainContext);

  /**
   * Function used to determine if the quiz is completed
   */
  const updateComplete = async () => {
    if (quiz._id && ["student", "parent"].includes(userRole)) {
      setIsComplete(await checkResultComplete("quizzes", quiz._id));
    }
  };

  /**
   * Function that executes update complete
   */
  useEffect(() => {
    updateComplete();
  }, [quiz._id]);

  return (
    <div
      className={`w-full h-[110px] px-6 py-5 bg-neutral-25 rounded-xl justify-between items-center inline-flex hover:shadow-card ease-in-out duration-150 relative cursor-pointer ${
        isComplete ? "border-2 border-success-500" : "border border-neutral-100"
      }`}
      onClick={handleExpand}
    >
      {isComplete && (
        <div className="absolute left-0 top-0 w-6 h-6">
          <div className="relative w-full h-full p-1 rounded-tl-lg rounded-br-xl bg-success-500">
            <MdCheck className="w-full h-full text-white" />
          </div>
        </div>
      )}
      <div className="h-[68px] justify-start items-center gap-3 flex">
        <ImageSkeleton
          src={`/api/imgs/quizzes/${quiz._id}/badgeImage`}
          alt={quiz.title}
          className="w-[68px] h-[68px] rounded-full relative overflow-hidden"
        />
        <div className="grow shrink basis-0 flex-col justify-center items-start gap-1 inline-flex">
          <div className="self-stretch text-neutral-700 text-lg font-semibold font-poppins leading-snug">
            {quiz.title}
          </div>
          <div className="self-stretch text-neutral-600 text-xs font-normal font-poppins leading-[18px]">
            {quiz.questions ? quiz.questions.length : 0} question
            {(quiz.questions ? quiz.questions.length : 0) > 1 && "s"}
          </div>
        </div>
      </div>
      <div className="flex justify-end items-center gap-5">
        {["master admin", "customer admin", "class admin"].includes(
          userRole,
        ) && (
          <div className="w-12 self-stretch justify-center items-center gap-3 flex">
            <button
              className={`p-3 bg-neutral-100 rounded-full justify-start items-start gap-2 flex z-20 ${
                quiz.canEdit
                  ? "hover:bg-neutral-200 ease-in-out duration-300 cursor-pointer"
                  : "cursor-not-allowed"
              }`}
              onClick={quiz.canEdit ? handleEdit : () => {}}
              title={
                quiz.canEdit ? "" : "This user is not allowed to edit this quiz"
              }
            >
              <div className="w-6 h-6">
                <MdOutlineEdit
                  className={`w-full h-full ${
                    quiz.canEdit ? "fill-neutral-700" : "fill-neutral-200"
                  }`}
                />
              </div>
            </button>
          </div>
        )}
        <div className="w-fit self-stretch justify-center items-center gap-3 flex">
          <div
            className="p-3 bg-neutral-100 rounded-full justify-start items-start gap-2 flex cursor-pointer hover:bg-neutral-200 ease-in-out duration-150"
            onClick={handleExpand}
          >
            <div className="w-6 h-6">
              <MdArrowForward className="w-full h-full fill-neutral-700" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuizListCard;
